import { Component, h, Prop, State, Event, EventEmitter } from '@stencil/core';
import { DateUtils, FeedItemSubType, FeedUtils, FileDoc, FileType, PersonDoc, SubmittalDoc, SubmittalFeedItemDoc, SubmittalRevision, WorkflowState } from 'common';
import { CommentSubmit } from '../../global/models/CommentSubmit';
import { FeedFilter, User } from '../../global';
import { SubmittalService } from '../services/SubmittalService';
import { SubmittalUtils } from '..';

@Component({
  tag: 'submittal-comments-card',
  styleUrl: 'concourse-submittals.css',
})
export class SubmittalCommentsCard {
  @Prop() projectId: string;
  @Prop() submittalId: string;
  @Prop() submittal: SubmittalDoc;
  @Prop() people: PersonDoc[];
  @Prop() files: FileDoc[];
  @Prop() feed: SubmittalFeedItemDoc[];
  @Prop() selectedRevisionIndex: number = -1;
  @Prop() canWrite: boolean = false;
  @Prop() isMobile: boolean = false;
  @Prop() activeFilter: FeedFilter = FeedFilter.ALL;
  @Prop() newComment: string = '';
  @Prop() feedLoadProgress: number = 0;

  @Event() setFilter: EventEmitter<FeedFilter>;
  @Event() commentSubmit: EventEmitter<CommentSubmit>;
  @Event() newCommentChange: EventEmitter<string>;
  @Event() feedSelectedChange: EventEmitter<SubmittalFeedItemDoc[]>;
  @Event() handleFinishSubmittalMarkup: EventEmitter<string>;
  @Event() setCommentsRef: EventEmitter<HTMLDivElement>;
  @Event() toggleAcceptedFile: EventEmitter<string>;

  @State() feedSelected: SubmittalFeedItemDoc[] = [];

  private commentsRef: HTMLDivElement;

  get user(): User {
    return globalThis.user;
  }

  get latestRevision(): SubmittalRevision {
    return this.submittal.revisions[this.submittal.revisions.length - 1];
  }

  get revision(): SubmittalRevision {
    if (this.selectedRevisionIndex === -1) {
      return this.latestRevision;
    }
    return this.submittal.revisions[this.selectedRevisionIndex];
  }

  get currentState(): WorkflowState {
    return SubmittalUtils.getCurrentState(this.revision);
  }

  componentDidLoad() {
    if (this.commentsRef) {
      this.setCommentsRef.emit(this.commentsRef);
    }
  }

  getFilterMapping() {
    return {
      all: null,
      comments: [FeedItemSubType.COMMENT, FeedItemSubType.EDIT_COMMENT],
      files: [FeedItemSubType.FILE, FeedItemSubType.PHOTO, FeedItemSubType.DRAWING, FeedItemSubType.SPECIFICATION],
      status: [FeedItemSubType.STATUS],
    };
  }

  getFilteredFeed(): SubmittalFeedItemDoc[] {
    if (this.activeFilter === FeedFilter.ALL) {
      return this.feed.sort(FeedUtils.sortOldestFirst);
    }

    const nonDeletedFeed = this.feed.filter(item => !item.deleted_by);
    const filterMapping = this.getFilterMapping();
    const allowedSubTypes = filterMapping[this.activeFilter];

    if (!allowedSubTypes) {
      return nonDeletedFeed;
    }

    return nonDeletedFeed.filter(item => allowedSubTypes.includes(item.sub)).sort(FeedUtils.sortOldestFirst);
  }

  getFeedItemButton(item: SubmittalFeedItemDoc) {
    const acceptable = [FeedItemSubType.FILE, FeedItemSubType.PHOTO, FeedItemSubType.COMMENT];
    if (!this.canWrite || !this.currentState || !acceptable.includes(item.sub) || item.deleted_by !== undefined) return null;

    const date = DateUtils.fromISO(item.date);
    if (item.user_id === this.user.id && DateUtils.isSameDay(date, DateUtils.currentDate)) {
      return (
        <ion-button fill="clear" onClick={() => this.deleteFeedItem(item)}>
          <ion-icon slot="icon-only" name="trash-outline"></ion-icon>
        </ion-button>
      );
    }

    const isAccepted = this.revision.accepted_answers?.includes(item._id);

    return <feed-item-accept-button isAccepted={isAccepted} onToggleAccepted={e => this.toggleAccepted(item._id)} />;
  }

  toggleAccepted(feedItemId: string) {
    this.toggleAcceptedFile.emit(feedItemId);
  }

  async deleteFeedItem(item: SubmittalFeedItemDoc) {
    const updatedItem = { ...item, deleted_by: this.user.id, deleted_date: DateUtils.currentIso };
    await SubmittalService.storeFeedItem(this.projectId, this.submittalId, updatedItem);
  }

  onNewCommentSubmit(comment: CommentSubmit) {
    this.commentSubmit.emit(comment);
    if (this.activeFilter === FeedFilter.FILES || this.activeFilter === FeedFilter.STATUS) {
      this.setFilter.emit(FeedFilter.ALL);
    }
  }

  render() {
    const header = (
      <ion-card-header class="submittal-panel-card__header ion-hide-md-down">
        <ion-card-title class="typography-body1-500">Feed</ion-card-title>
      </ion-card-header>
    );

    if (!this.feed) {
      return (
        <div class="submittal-comments-card">
          {header}
          <div class="submittal-comments-card__body ion-padding">Loading feed {this.feedLoadProgress > 0 ? `(${Math.round(this.feedLoadProgress * 100)}%)` : '...'}</div>
        </div>
      );
    }
    if (!this.files) {
      return (
        <div class="submittal-comments-card">
          {header}
          <div class="submittal-comments-card__body ion-padding">Loading files...</div>
        </div>
      );
    }
    const filteredFeed = this.getFilteredFeed();
    return (
      <div class="submittal-comments-card">
        {header}
        <div class="submittal-comments-card__body ion-display-flex ion-flex-column">
          <feed-filter-buttons
            activeFilter={this.activeFilter}
            acceptedAnswersCount={this.revision.accepted_answers?.length || 0}
            isMobile={this.isMobile}
            onFilterChange={e => this.setFilter.emit(e.detail)}
          />
          <div class="flex-grow cc-scrollbars ion-padding-start workflow-view-detail-feed-container" ref={el => (this.commentsRef = el as HTMLDivElement)}>
            {filteredFeed.length === 0 ? (
              <p>No items found for the selected filter.</p>
            ) : (
              <concourse-feed
                projectId={this.projectId}
                people={this.people}
                files={this.files}
                feed={filteredFeed}
                groupCommonItems={false}
                onItemsSelected={e => {
                  this.feedSelected = e.detail as SubmittalFeedItemDoc[];
                  this.feedSelectedChange.emit(this.feedSelected);
                }}
                buttonCallback={this.isMobile ? null : this.getFeedItemButton.bind(this)}
              />
            )}
          </div>
          {this.canWrite && (
            <div class="cc-padding-horizontal cc-padding-bottom">
              {this.feedSelected.length > 0 ? (
                <div class="ion-display-flex ion-align-items-center" style={{ height: '50px' }}>
                  <span style={{ color: '#666', flex: '1' }}>{this.feedSelected.length} comments selected</span>
                  <ion-button fill="clear" onClick={() => console.log(`Delete selected items: ${this.feedSelected.map(item => item._id).join(', ')}`)}>
                    <ion-icon slot="icon-only" name="trash-outline"></ion-icon>
                  </ion-button>
                </div>
              ) : (
                <concourse-comment
                  projectId={this.projectId}
                  feature="submittals"
                  featureId={this.submittalId}
                  placeholder="Enter comment"
                  rows={`${this.isMobile ? 1 : 3}`}
                  isMobile={this.isMobile}
                  value={this.newComment}
                  fileRoles={SubmittalUtils.getReadRoles(this.projectId, this.submittalId)}
                  fileType={FileType.DOCUMENT}
                  onMessageChange={(e: CustomEvent) => this.newCommentChange.emit(e.detail)}
                  onCommentSubmit={(e: CustomEvent<CommentSubmit>) => this.onNewCommentSubmit(e.detail)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}
